import { msToJalali, jalaliToMs } from '../../lib/jalali';
import { addDaysIso, clinicTodayIso, isoDayOf, wallToMs } from '../../lib/clinicZone';
import { DOC_COLORS, START_HOUR, DAY_MIN } from './constants';

export function getInitials(name = '') {
  const w = name.trim().split(/\s+/);
  return w.length >= 2 ? (w[0][0] || '') + (w[w.length - 1][0] || '') : (w[0] || '?')[0];
}

// ── The booking grid's day and hour, on the CLINIC's clock (PM-0088) ──────────
//
// A grid day travels as a Date in one of two shapes: a CALENDAR date (UTC midnight
// of that Gregorian day — what `jalaliToDate` returns) or an INSTANT («now», a
// slot's own dateTime). `dayIsoOf` reads either as the clinic's calendar day, and
// every slot instant is built from that day and the slot's wall time with
// `slotInstant` — never with `setHours`, which used the BROWSER's zone: a desk
// whose computer was not on Tehran time booked every slot off by its offset.

const isUtcMidnight = (d) => d.getUTCHours() === 0 && d.getUTCMinutes() === 0
  && d.getUTCSeconds() === 0 && d.getUTCMilliseconds() === 0;

/** The clinic calendar day ('YYYY-MM-DD') a grid Date stands for. */
export function dayIsoOf(day) {
  const d = day instanceof Date ? day : new Date(Number(day));
  if (Number.isNaN(d.getTime())) return '';
  return isUtcMidnight(d) ? d.toISOString().slice(0, 10) : isoDayOf(d.getTime());
}

/** 'YYYY-MM-DD' → the CALENDAR Date the grid passes around. */
export function dayDateOf(iso) { return new Date(`${iso}T00:00:00Z`); }

/** The instant the clinic's clock reads h:m on that grid day. */
export function slotInstant(day, h, m = 0) { return wallToMs(dayIsoOf(day), h * 60 + m); }

/** The day of week (0 = Sunday, as Date#getDay) of that grid day. */
export function calendarDow(day) { return new Date(`${dayIsoOf(day)}T12:00:00Z`).getUTCDay(); }

/** True when the grid day is «امروز» at the clinic. */
export function isClinicToday(day, nowMs = Date.now()) { return dayIsoOf(day) === clinicTodayIso(nowMs); }

/** The clinic's [midnight, next midnight) of a grid day, as the epoch-ms strings
 * `scheduledAppointments` takes — the window every slot of that day falls in. */
export function clinicDayWindow(day) {
  const iso = dayIsoOf(day);
  return { startTime: String(wallToMs(iso, 0)), endTime: String(wallToMs(addDaysIso(iso, 1), 0) - 1) };
}

export function dateToJalali(date) {
  const iso = dayIsoOf(date);
  return (iso && msToJalali(Date.parse(`${iso}T00:00:00Z`))) || { y: 1404, m: 1, d: 1 };
}

export function jalaliToDate(jy, jm, jd) { return new Date(jalaliToMs(jy, jm, jd)); }

// `formatDate` is threaded in — these are plain helpers, not components. Each
// used to hand-roll a Jalali date from its own weekday and month arrays, so it
// rendered Persian in every language; the named shapes render the same sentence
// in the reader's own calendar.
export function fmtJalaliLong(date, formatDate) {
  return formatDate(date, 'full');
}

export function doctorColor(doctorId = '') {
  let hash = 0;
  for (const ch of doctorId) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
  return DOC_COLORS[hash % DOC_COLORS.length];
}

// The first grid point of the day: START_HOUR stepped back by whole visits until one
// more step would cross midnight. 0 for 10/15/20/30/60; 00:30 for 45; 00:05 for 19.
function gridStartMin(step) {
  return (START_HOUR * 60) % step;
}

// Time slots for a given visit-duration interval (minutes), anchored at START_HOUR and
// covering [fromMin, toMin) of the day — by default all of it. A doctor's grid is then
// trimmed by presenceSlots, so their shift, not a fixed window, decides which hours are
// offered: a shift signed until 23:00 gets its evening, one from 06:00 its morning.
export function buildDoctorSlots(intervalMin = 30, fromMin = 0, toMin = DAY_MIN) {
  const step = Math.max(1, Math.floor(intervalMin) || 30);
  const slots = [];
  for (let abs = gridStartMin(step); abs < toMin; abs += step) {
    if (abs < fromMin) continue;
    const h = Math.floor(abs / 60);
    const m = abs % 60;
    slots.push({ h, m, label: `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}` });
  }
  return slots;
}

// JS getDay() (0=Sun…6=Sat) → Iranian dow (0=Sat…6=Fri)
export function jsToIrDow(jsDay) { return (jsDay + 1) % 7; }

// The doctor's presence windows on an Iranian dow as [start, end) minutes, with
// touching or overlapping ones merged: a shift entered as 09:00–12:00 + 12:00–15:00 is
// one continuous morning, not two that end and begin at noon. Same rule as
// _merge_windows in the scheduling service.
function presenceWindowsOn(presences = [], irDow) {
  const windows = presences
    .filter(p => p.dayOfWeek === irDow)
    .map(p => {
      const [sh, sm] = p.startTime.split(':').map(Number);
      const [eh, em] = p.endTime.split(':').map(Number);
      return [sh * 60 + sm, eh * 60 + em];
    })
    .sort((a, b) => a[0] - b[0]);
  const merged = [];
  for (const [start, end] of windows) {
    const last = merged[merged.length - 1];
    if (last && start <= last[1]) last[1] = Math.max(last[1], end);
    else merged.push([start, end]);
  }
  return merged;
}

// True if a visit starting at h:m STARTS inside a presence window on the given Iranian
// dow — the rule the scheduling service books by (_visit_starts_in_presence). Its end is
// not checked: a doctor signed 11:00–21:00 with 45-minute visits sees their last patient
// at 20:45, and that visit runs to 21:30. The window end is exclusive — at 21:00 the
// doctor has left.
export function slotInPresence(h, m, presences = [], irDow) {
  const start = h * 60 + m;
  return presenceWindowsOn(presences, irDow).some(([ws, we]) => ws <= start && start < we);
}

// True when the doctor has registered no shift at all in this clinic. Kept apart from
// doctorAbsentOn because the two are fixed in different places: an empty schedule is
// corrected once, in «پرسنل»; a day off is corrected by picking another day.
export function doctorHasNoShift(presences) {
  return !Array.isArray(presences) || presences.length === 0;
}

// True when the doctor cannot be booked on this Iranian dow — either they have no shift
// at all, or they have shifts but none that day.
//
// An empty schedule used to read as "available", so the grid offered every slot from
// 08:00 to 20:00 for a doctor who had never signed a shift and the booking went through.
// It is not availability, it is a question nobody answered yet.
export function doctorAbsentOn(presences, irDow) {
  if (doctorHasNoShift(presences)) return true;
  return !presences.some(p => p.dayOfWeek === irDow);
}

// The slots a doctor can actually be booked into on a given day: those that start inside
// a presence window (the last visit may run past its end). There is NO "show everything" fallback — a slot the doctor is not present for
// is not a slot, and offering it only teaches the desk that the grid lies (the server
// refuses it at booking anyway). No presences at all → no slots, matching what the
// scheduling service now does with an empty schedule.
export function presenceSlots(slots, presences, irDow) {
  if (doctorHasNoShift(presences)) return [];
  return slots.filter(s => slotInPresence(s.h, s.m, presences, irDow));
}

// Drop slots that collide with an already-booked appointment. `booked` is a list of
// { startMs, durMin } for the doctor on this day; a grid slot [start, start+slotMin) is
// removed when it overlaps any of them. Booked durations default to slotMin — appointments
// are created on the same grid, so an aligned same-width overlap is just a shared start.
export function freeSlots(slots, dayBaseDate, slotMin, booked) {
  if (!booked || booked.length === 0) return slots;
  const slotMs = slotMin * 60000;
  return slots.filter(s => {
    const startMs = slotInstant(dayBaseDate, s.h, s.m);
    const endMs = startMs + slotMs;
    return !booked.some(b => {
      const bEnd = b.startMs + (b.durMin || slotMin) * 60000;
      return startMs < bEnd && endMs > b.startMs;
    });
  });
}

// Round a "HH:MM" string down to the slot that CONTAINS it on the same grid
// buildDoctorSlots renders — which is anchored at START_HOUR, not at midnight.
// The distinction only became visible once a custom visit length was allowed:
// 10/15/20/30 all divide START_HOUR*60, so flooring from midnight happened to
// land on the same labels. 19 does not — an 08:00 appointment floored from
// midnight yields "07:55", a key no slot carries, and the appointment silently
// disappears from the calendar. Anchor at START_HOUR and every interval works.
export function roundToVisitSlot(timeStr, intervalMin = 30) {
  if (!timeStr) return null;
  const [h, m] = timeStr.split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  const step = Math.max(1, Math.floor(intervalMin) || 30);
  const first = gridStartMin(step);
  const abs = h * 60 + m;
  // Before the day's first grid point there is no slot to snap to; report the
  // time unchanged rather than inventing one above it.
  if (abs < first) return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  const rounded = first + Math.floor((abs - first) / step) * step;
  const rh = Math.floor(rounded / 60);
  const rm = rounded % 60;
  if (rh >= 24) return null;
  return `${String(rh).padStart(2, '0')}:${String(rm).padStart(2, '0')}`;
}

// Short Jalali date label: "۱ فروردین"
export function fmtJalaliShort(date, formatDate) {
  return formatDate(date, 'dayMonth');
}

/**
 * How one bookable service reads in the «خدمت» picker.
 *
 * «ویزیت تخصصی — ۲٬۵۰۰٬۰۰۰ ت (قیمت شخصی)». The price is part of the LABEL and
 * not a second column, because the desk is choosing between services partly on
 * what the patient will pay, and a price one line away from the name is a price
 * the eye has to pair up.
 *
 * A service the clinic has declared but not priced says so rather than showing
 * «۰ ت»: a price nobody has set is not a price of nothing
 * (docs/CLINIC_RECEPTION_SERVICE_INVOICE_DESIGN.md §S1).
 */
export function serviceLabelOf(svc, t, formatMoney) {
  if (!svc) return '';
  const name = svc.description || '';
  if (!(svc.unitPrice > 0)) return `${name} — ${t('calendar.booking.serviceNoPrice')}`;
  const price = t('calendar.booking.servicePrice', { amount: formatMoney(svc.unitPrice, { currency: svc?.currency }) });
  return svc.priceIsPersonal
    ? `${name} — ${price} (${t('calendar.booking.personalPrice')})`
    : `${name} — ${price}`;
}

/**
 * The services a booking may PLAN beside its own «خدمت نوبت» — one chip per
 * catalog service, priced for THIS doctor.
 *
 * `clinicServiceDeclarations` is not one row per service. A clinic-wide price
 * (`doctorId` null) can sit beside a personal price for each practitioner, and
 * since the doctor↔clinic contract was removed every practitioner's visit is
 * exactly that: their own declaration on the shared `consult-visit` catalog row.
 * Rendered as-is, the chips listed «ویزیت» once per doctor under one React key,
 * and the quote priced a pick from whichever doctor's row came first.
 *
 * So, per catalog id: this doctor's own row wins, else the clinic-wide one;
 * another practitioner's personal price never applies here. A CONSULTATION is
 * not plannable at all — the visit is the appointment's own service (chosen in
 * «خدمت نوبت» and priced from the practitioner's declaration), so planning it
 * again would quote the visit twice. Inactive and unpriced rows are dropped:
 * a chip with nothing to add to the proforma is not a choice.
 */
export function plannableServices(declared, doctorId) {
  const byCatalog = new Map();
  for (const svc of declared || []) {
    if (!svc?.catalogId || svc.isActive === false || !(svc.unitPrice > 0)) continue;
    if (svc.orderType === 'consultation') continue;
    const personal = !!svc.doctorId;
    if (personal && svc.doctorId !== doctorId) continue;
    const cur = byCatalog.get(svc.catalogId);
    // A personal row replaces a clinic-wide one; the first of two equals stays.
    if (!cur || (personal && !cur.doctorId)) byCatalog.set(svc.catalogId, svc);
  }
  return [...byCatalog.values()];
}
